import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  Heading,
  Select,
  VStack,
  Box,
  HStack,
  Divider,
  Spinner,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { mediaType, templateTypes, whatsappCategories } from '../../_helpers/data_helper';
import MobilePreview from '../Campaigns/components/MobilePreview';
import PrimaryButton from '../../components/themeComponents/PrimaryButton';
import { useToast } from '@chakra-ui/react';
import {
  getWhatsappTemplateDetails,
  postWhatsappTemplate,
  updateWhatsappTemplate,
  whatsappChannelAccess,
} from '../../Services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import Confirmation from '../../components/themeComponents/Confirmation';
import { whatsappTemplateValidation } from '../../_helpers/validationSchema';
import { FormErrorMessage } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import ShortenUrlModal from '../../components/ShortenUrlModal';
import ImageGallery from '../../components/ImageGallery';
import PlaceholderInserter from '../../components/themeComponents/PlaceholderInserter';

const initialValues = {
  mediaType: '',
  templateName: '',
  templateDisplayName: '',
  message: '',
  language: 'en',
  templateType: '',
  templateId: '',
  templateDescription: '',
  attachments: '',
  variables: [],
  variablesCount: 0,
  buttons: [],
  sample_url: '',
  sampleBody: '',
  footer: '',
  category: '',
  header_content: '',
  sample_header_content: '',
  uuid: ''
};

const WhatsappTemplate = () => {
  const [providerName, setProviderName] = useState('');
  const [checkAccess, setCheckAccess] = useState(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [isShortenUrlModalOpen, setIsShortenUrlModalOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const uuid = window.location.pathname.split('/')[3];

  useEffect(() => {
    const providerFromState = location.state?.providerName || window.history.state?.usr?.providerName;
    if (providerFromState) {
      setProviderName(providerFromState.toLowerCase());
    }
  }, [location]);

  const isKaleyra = providerName === 'kaleyra';

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: whatsappTemplateValidation,
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = async (values) => {
    let saveResponse = null;

    try {
      if (!!values?.variables?.length) {
        values.variablesCount = values.variables.length;
      }
      if (location?.state?.uuid || uuid) {
        saveResponse = await updateWhatsappTemplate(values, location.state.uuid || uuid);
      } else {
        
        saveResponse = await postWhatsappTemplate(values);
      }

      if (saveResponse) {
        setShowAlert(true);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save template. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      formik.setSubmitting(false);
    }
  };

  const getWhatsappTemplateDetail = async (templateUuid, providerUuid) => {
    try {
      const res = await getWhatsappTemplateDetails(templateUuid, providerUuid);

      if (res?.data?.templates) {
        let template = res.data.templates;

        try {
          template.buttons = JSON.parse(template.buttons || '[]');
          template.variables = JSON.parse(template.variables || '[]');
        } catch (e) {
          template.buttons = [];
          template.variables = [];
        }

        formik.setValues(template);
      }
    } catch (err) {
      console.error("Failed to load template:", err);
      toast({
        title: 'Error',
        description: 'Could not load template details.',
        status: 'error',
      });
    }
  };

  const checkWhatsappAccess = async () => {
    try {
      setIsLoadingAccess(true);
      const response = await whatsappChannelAccess();
      setCheckAccess(response.data.hasWhatsAppAccess);
    } catch (error) {
      console.error("Error checking WhatsApp access", error);
      setCheckAccess(false);
    } finally {
      setIsLoadingAccess(false);
    }
  };

  useEffect(() => {
    checkWhatsappAccess();

    if (location?.state?.uuid || uuid) {
      getWhatsappTemplateDetail(
        uuid || location?.state?.uuid,
        location?.state?.providerUuid || window?.history?.state?.provider
      );
    }
  }, []);

  const addVariableToBody = () => {
    const regex = /{{([^{}]*)}}/g;
    const matches = formik.values.message.match(regex) || [];
    const nextIndex = matches.length + 1;
    const newVar = `{{${nextIndex}}}`;

    formik.setFieldValue('message', (formik.values.message + ' ' + newVar).trim());
    formik.setFieldValue('variables', [...(formik.values.variables || []), newVar]);
  };

  const removeButton = (index) => {
    formik.setFieldValue('buttons', formik.values.buttons.filter((_, i) => i !== index));
  };

  const addButton = (type) => {
    const newButton = type === 'url'
      ? {
          type: 'url',
          url: '',
          button_name: '',
          button_url_type: 'static',
        }
      : {
          type: 'quick_reply',
          quick_reply: '',
        };

    formik.setFieldValue('buttons', [...(formik.values.buttons || []), newButton]);
  };

  const quickReplyCount = (formik.values.buttons || []).filter(b => b.type === 'quick_reply').length;

  if (isLoadingAccess) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minH="80vh">
        <VStack spacing={6}>
          <Spinner thickness="5px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
          <Text fontSize="lg" color="gray.600" fontWeight="medium">
            Checking WhatsApp Channel Access...
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <HStack p={6} align="start" spacing={10} minH="100vh">
      {checkAccess === true ? (
        <>
          <Box width="62%" maxW="780px">
            <Heading as="h3" size="lg" mb={6}>
              {location?.state?.uuid || uuid ? 'Edit' : 'Create'} WhatsApp Template
            </Heading>

            <Text mb={3} fontWeight="medium" color="gray.700">Template Information</Text>
            <Divider mb={8} />

            <form onSubmit={formik.handleSubmit}>
              <VStack spacing={8} align="stretch">

                {/* Basic Info */}
                <HStack spacing={6} align="start">
                  <FormControl isRequired isInvalid={formik.touched.templateName && formik.errors.templateName}>
                    <FormLabel>Template Name</FormLabel>
                    <Input
                      bg="#EEEEEE"
                      borderRadius="16px"
                      placeholder="e.g. order_confirmation_v1"
                      {...formik.getFieldProps('templateName')}
                    />
                    <FormErrorMessage>{formik.errors.templateName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formik.touched.templateDisplayName && formik.errors.templateDisplayName}>
                    <FormLabel>Display Name</FormLabel>
                    <Input
                      bg="#EEEEEE"
                      borderRadius="16px"
                      placeholder="e.g. Order Confirmation"
                      {...formik.getFieldProps('templateDisplayName')}
                    />
                    <FormErrorMessage>{formik.errors.templateDisplayName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={formik.touched.language && formik.errors.language}>
                    <FormLabel>Language</FormLabel>
                    <Select bg="#EEEEEE" borderRadius="16px" {...formik.getFieldProps('language')}>
                      <option value="en">English</option>
                      <option value="hi">Hindi</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="ar">Arabic</option>
                    </Select>
                    <FormErrorMessage>{formik.errors.language}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack spacing={6}>
                  <FormControl isRequired isInvalid={formik.touched.category && formik.errors.category}>
                    <FormLabel>Category</FormLabel>
                    <Select bg="#EEEEEE" borderRadius="16px" placeholder="Select Category" {...formik.getFieldProps('category')}>
                      {whatsappCategories.map((cat) => (
                        <option key={cat.key} value={cat.key}>{cat.label}</option>
                      ))}
                    </Select>
                    <FormErrorMessage>{formik.errors.category}</FormErrorMessage>
                  </FormControl>
                </HStack>

                {/* Header */}
                <HStack spacing={6} align="start">
                  <FormControl isRequired isInvalid={formik.touched.templateType && formik.errors.templateType}>
                    <FormLabel>Header Type</FormLabel>
                    <Select bg="#EEEEEE" borderRadius="16px" placeholder="Select Header" {...formik.getFieldProps('templateType')}>
                      {templateTypes.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </Select>
                    <FormErrorMessage>{formik.errors.templateType}</FormErrorMessage>
                  </FormControl>

                  {formik.values.templateType !== 'text' && (
                    <FormControl isInvalid={formik.touched.mediaType && formik.errors.mediaType}>
                      <FormLabel>Media Type</FormLabel>
                      <Select bg="#EEEEEE" borderRadius="16px" placeholder="Select Media" {...formik.getFieldProps('mediaType')}>
                        {mediaType.map((m) => (
                          <option key={m.key} value={m.key}>{m.label}</option>
                        ))}
                      </Select>
                      <FormErrorMessage>{formik.errors.mediaType}</FormErrorMessage>
                    </FormControl>
                  )}
                </HStack>

                <FormControl>
                  <FormLabel>Header Text (Optional)</FormLabel>
                  <Input bg="#EEEEEE" borderRadius="16px" {...formik.getFieldProps('header_content')} />
                </FormControl>

                {isKaleyra && (
                  <FormControl>
                    <FormLabel color="blue.600">Sample Header Content</FormLabel>
                    <Input bg="#EEEEEE" borderRadius="16px" {...formik.getFieldProps('sample_header_content')} />
                  </FormControl>
                )}

                {formik.values.templateType !== 'text' && (
                  <FormControl>
                    <FormLabel>Media Attachment</FormLabel>
                    <InputGroup>
                      <Input
                        bg="#EEEEEE"
                        borderRadius="16px"
                        placeholder="https://example.com/image.jpg   or   {{media_variable}}   or   %asset_123%"
                        {...formik.getFieldProps('attachments')}
                      />
                      <InputRightElement width="auto" pr={3}>
                        <PlaceholderInserter
                          setFieldValue={(value) => formik.setFieldValue('attachments', value)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Use direct URL, variable placeholder or uploaded asset
                    </Text>
                    <Box mt={2}>
                      <ImageGallery
                        setImageData={(e) => formik.setFieldValue('attachments', e.mediaFileUrl)}
                        renderComponent={<Text cursor="pointer" color="blue.600" textDecor="underline">Upload Media</Text>}
                      />
                    </Box>
                  </FormControl>
                )}

                {/* Message Body + Variables */}
                <FormControl  isInvalid={formik.touched.message && formik.errors.message}>
                  <HStack justify="space-between" align="center" mb={3}>
                    <FormLabel mb={0}>Message Body</FormLabel>
                    <Text
                      fontSize="sm"
                      color="blue.600"
                      cursor="pointer"
                      textDecor="underline"
                      onClick={addVariableToBody}
                    >
                      + Add Variable
                    </Text>
                  </HStack>

                  <Textarea
                    bg="#EEEEEE"
                    borderRadius="16px"
                    resize="vertical"
                    minH="140px"
                    {...formik.getFieldProps('message')}
                  />

                  <HStack mt={3} spacing={6}>
                    <Text
                      fontSize="sm"
                      color="blue.600"
                      cursor="pointer"
                      textDecor="underline"
                      onClick={() => setIsShortenUrlModalOpen(true)}
                    >
                      Shorten URL
                    </Text>
                  </HStack>

                  <FormErrorMessage mt={2}>{formik.errors.message}</FormErrorMessage>

                  {/* Variables List */}
                  {formik.values.variables?.length > 0 && (
                    <Box mt={6}>
                      <Text fontSize="sm" fontWeight="medium" mb={3}>Variables used in message:</Text>
                      <VStack spacing={4} align="stretch">
                        {formik.values.variables.map((v, i) => (
                          <HStack key={i} spacing={4} align="start">

                            <FormLabel minW="110px" fontSize="sm" mb={0} mt={2}>
                              Variable {i + 1}
                            </FormLabel>

                            <Box flex={1} position="relative">
                              <Textarea
                                size="sm"
                                bg="#F7FAFC"
                                value={v}
                                minH="100px"
                                resize="vertical"
                                pr="50px"
                                borderRadius="md"
                                placeholder={`Enter value for Variable ${i + 1}`}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    `variables[${i}]`,
                                    e.target.value
                                  )
                                }
                              />

                              <Box position="absolute" top="8px" right="8px">
                                <PlaceholderInserter
                                  setFieldValue={(val) =>
                                    formik.setFieldValue(`variables[${i}]`, val)
                                  }
                                />
                              </Box>
                            </Box>

                            <Text
                              color="red.500"
                              fontSize="sm"
                              cursor="pointer"
                              _hover={{ textDecor: 'underline' }}
                              mt={2}
                              onClick={() => {
                                const placeholder = `{{${i + 1}}}`;

                                const cleaned = formik.values.message
                                  .replace(placeholder, '')
                                  .trim();

                                formik.setFieldValue('message', cleaned);

                                formik.setFieldValue(
                                  'variables',
                                  formik.values.variables.filter(
                                    (_, idx) => idx !== i
                                  )
                                );
                              }}
                            >
                              Remove
                            </Text>

                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Footer (Optional)</FormLabel>
                  <Input bg="#EEEEEE" borderRadius="16px" {...formik.getFieldProps('footer')} />
                </FormControl>

                {/* Buttons */}
                <Box>
                  <Text fontWeight="medium" mb={4} fontSize="lg">Interactive Buttons</Text>

                  <HStack spacing={4} mb={6}>
                    <PrimaryButton
                      size="sm"
                      onClick={() => addButton('url')}
                      label="Add Call-to-Action (URL)"
                    />
                    <PrimaryButton
                      size="sm"
                      variant="outline"
                      onClick={() => addButton('quick_reply')}
                      isDisabled={quickReplyCount >= 3}
                      label={`Add Quick Reply ${quickReplyCount >= 3 ? '(max 3)' : ''}`}
                    />
                  </HStack>

                  {(formik.values.buttons || []).map((btn, index) => (
                    <Box
                      key={index}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="xl"
                      p={6}
                      mb={6}
                      bg="gray.50"
                      position="relative"
                    >
                      <Text
                        position="absolute"
                        top={4}
                        right={5}
                        color="red.500"
                        fontSize="sm"
                        fontWeight="medium"
                        cursor="pointer"
                        _hover={{ opacity: 0.8 }}
                        onClick={() => removeButton(index)}
                      >
                        Remove
                      </Text>

                      <FormControl mb={5}>
                        <FormLabel fontSize="md">Button Type</FormLabel>
                        <Text fontWeight="semibold" color={btn.type === 'url' ? 'blue.600' : 'green.600'}>
                          {btn.type === 'url' ? 'Call to Action (URL)' : 'Quick Reply'}
                        </Text>
                      </FormControl>

                      {btn.type === 'url' && (
                        <VStack spacing={5} align="stretch">
                          <HStack spacing={5}>
                            <FormControl flex={1}>
                              <FormLabel>URL Type</FormLabel>
                              <Select
                                bg="#EEEEEE"
                                borderRadius="16px"
                                value={btn.button_url_type || 'static'}
                                onChange={(e) => formik.setFieldValue(`buttons[${index}].button_url_type`, e.target.value)}
                              >
                                <option value="static">Static</option>
                                <option value="dynamic">Dynamic</option>
                              </Select>
                            </FormControl>

                            <FormControl flex={2}>
                              <FormLabel>URL</FormLabel>
                              <InputGroup>
                                <Input
                                  bg="#EEEEEE"
                                  borderRadius="16px"
                                  placeholder={btn.button_url_type === 'dynamic' ? 'https://example.com/{{1}}' : 'https://example.com/'}
                                  value={btn.url || ''}
                                  onChange={(e) => formik.setFieldValue(`buttons[${index}].url`, e.target.value)}
                                />
                                <InputRightElement width="auto" pr={3}>
                                  <PlaceholderInserter
                                    setFieldValue={(val) => formik.setFieldValue(`buttons[${index}].url`, val)}
                                  />
                                </InputRightElement>
                              </InputGroup>
                            </FormControl>
                          </HStack>

                          <FormControl>
                            <FormLabel>Button Text (max 25 chars)</FormLabel>
                            <Input
                              bg="#EEEEEE"
                              borderRadius="16px"
                              maxLength={25}
                              placeholder="e.g. View Details, Shop Now"
                              value={btn.button_name || ''}
                              onChange={(e) => formik.setFieldValue(`buttons[${index}].button_name`, e.target.value)}
                            />
                          </FormControl>
                        </VStack>
                      )}

                      {btn.type === 'quick_reply' && (
                        <FormControl>
                          <FormLabel>Quick Reply Text (max 20 chars)</FormLabel>
                          <InputGroup>
                            <Input
                              bg="#EEEEEE"
                              borderRadius="16px"
                              maxLength={20}
                              placeholder="e.g. Yes, No, Track Order"
                              value={btn.quick_reply || ''}
                              onChange={(e) => formik.setFieldValue(`buttons[${index}].quick_reply`, e.target.value)}
                            />
                            <InputRightElement width="auto" pr={3}>
                              <PlaceholderInserter
                                setFieldValue={(val) => formik.setFieldValue(`buttons[${index}].quick_reply`, val)}
                              />
                            </InputRightElement>
                          </InputGroup>
                          <Text fontSize="xs" color="gray.500" mt={2}>
                            WhatsApp recommends keeping quick replies short
                          </Text>
                        </FormControl>
                      )}
                    </Box>
                  ))}

                  {formik.errors.buttons && typeof formik.errors.buttons === 'string' && (
                    <Text color="red.500" fontSize="sm" mt={3}>
                      {formik.errors.buttons}
                    </Text>
                  )}
                </Box>

                <HStack mt={12} justify="flex-start">
                  <PrimaryButton
                    type="submit"
                    isLoading={formik.isSubmitting}
                    isDisabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                    label={location?.state?.uuid || uuid ? 'Update Template' : 'Save Template'}
                  />
                </HStack>
              </VStack>
            </form>
          </Box>

          <Box position="fixed" right="5vw" top="10vh" zIndex={10} boxShadow="2xl" borderRadius="xl">
            <MobilePreview
              channel="whatsapp"
              message={formik.values.message}
              header={formik.values.header_content}
              footer={formik.values.footer}
              attachment={formik.values.attachments}
              buttons={formik.values.buttons}
            />
          </Box>
        </>
      ) : (
        <Box textAlign="center" mt={20} w="100%">
          <Text fontSize="xl" fontWeight="medium" color="gray.700">
            No <Text as="span" fontStyle="italic">WhatsApp Provider</Text> Integrated
          </Text>
          <Link to="/settings/whatsapp">
            <Text color="blue.600" fontSize="lg" textDecor="underline" mt={6} display="inline-block">
              Connect WhatsApp Provider →
            </Text>
          </Link>
        </Box>
      )}

      <Confirmation
        isOpen={showAlert}
        title={`Template ${location?.state?.uuid || uuid ? 'Updated' : 'Created'} Successfully!`}
        text="Do you want to create another template?"
        confirmButtonText="Yes, Create Another"
        cancelButtonText="Go to Template List"
        onConfirm={() => {
          setShowAlert(false);
          formik.resetForm();
          window.scrollTo(0, 0);
        }}
        onCancel={() => {
          navigate('/whatsapp/list');
          setShowAlert(false);
        }}
      />

      <ShortenUrlModal
        isOpen={isShortenUrlModalOpen}
        mainFormik={formik}
        onClose={() => setIsShortenUrlModalOpen(false)}
      />
    </HStack>
  );
};

export default WhatsappTemplate;